/**
 * Préstamos Personales — migración web de ares2010ns (escritorio).
 *
 * Préstamo de dinero sin vehículo: el backend inserta factmot -> solicit ->
 * prestamo -> amort con prestamo.ppersonal='S' (los reportes distinguen por
 * esa marca). prestamo.cantsol = deuda total (capital + intereses), la misma
 * convención que los préstamos de vehículos y los refinanciamientos.
 * Cartera, oficial de crédito y fiador son obligatorios; la garantía es texto
 * libre. Si varinicial.usachasisenpersonal='S' el préstamo puede garantizarse
 * con un chasis del inventario (queda marcado como vendido) — la validación
 * de chasis del escritorio estaba rota por un NameError y aquí funciona.
 * Interés mensual fuera del rango [interesminimo, 20] exige autorización
 * (mismo flujo de autorizaciones de la venta financiada, con polling).
 */
import React, { useState, useEffect, useRef, useMemo } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import Autocomplete from "@mui/material/Autocomplete";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, DocumentoField, fpInputSx } from "components/financepro";
import { documentoValido } from "lib/documento";
import { useApi } from "lib/api";
import { calcularCuotas, useConfigCuotas, mananaISO, unMesISO } from "lib/calculocuotas";
import CampoCuota from "components/cuotas/CampoCuota";

const FORM_INICIAL = {
  // Cliente
  cedula: "",
  nombres: "",
  apellidos: "",
  direccion: "",
  provincia: "",
  telefono: "",
  celular: "",
  email: "",
  apodo: "",
  edad: "",
  // Préstamo
  monto: "",
  formadepago: "",
  fechaprimeracuota: "",
  interes: "",
  plazo: "",
  idcartera: "",
  idoficial: "",
  garantia: "",
  comentario: "",
  // Fiador
  fiador: "",
  fiadorced: "",
  fiadortel: "",
  fiadordir: "",
  // Garantía con chasis (solo si usachasisenpersonal='S')
  chasis: "",
};

const fmtMoney = (v) => {
  const n = typeof v === "number" ? v : parseFloat(String(v || "").replace(/,/g, ""));
  if (Number.isNaN(n)) return "0.00";
  return n.toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const toNum = (v) => {
  const n = parseFloat(String(v ?? "").replace(/,/g, ""));
  return Number.isNaN(n) ? 0 : n;
};

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});
const btnGhost = {
  ...btnBase,
  color: FP.color.textSecondary,
  bgcolor: "transparent",
  "&:hover": { bgcolor: FP.color.surfaceAlt, color: FP.color.textPrimary },
};

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function SectionTitle({ icon: Icon, children, color, extra }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.4,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: `${color}14`,
          color,
        }}
      >
        <Icon sx={{ fontSize: 19 }} />
      </Box>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: "0.74rem",
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: FP.color.textSecondary,
        }}
      >
        {children}
      </Typography>
      {extra}
    </Box>
  );
}

export default function PrestamosPersonales() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const { redondeo, elegirPrimeraCuota } = useConfigCuotas();

  const [form, setForm] = useState(FORM_INICIAL);
  const setF = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  // Configuración del tenant (provincias, carteras, oficiales, límites).
  const [config, setConfig] = useState(null);
  useEffect(() => {
    let activo = true;
    post("/api/prestamopersonal_config", {}).then(({ ok, data }) => {
      if (!activo) return;
      if (ok) setConfig(data);
      else
        showAlert({ message: data.Error || "No se pudo cargar la configuración", color: "error" });
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const provincias = config?.provincias || [];
  const formasPago = config?.formasPago || ["Meses", "Quincenas", "Semanas", "Dias"];
  const carteras = config?.carteras || [];
  const oficiales = config?.oficiales || [];
  const interesminimo = config?.interesminimo ?? 3;
  const usaChasis = config?.usachasisenpersonal === "S";
  const sinCatalogos = config && (carteras.length === 0 || oficiales.length === 0);

  // ---- Cliente ----
  const [pendiente, setPendiente] = useState(0);
  const cedulaBuscadaRef = useRef("");
  // Documento del cliente y del fiador: cédula, RNC o pasaporte, guardados en
  // la misma columna cedula (el tipo solo pone la máscara y la validación).
  const [tipodoc, setTipodoc] = useState("cedula");
  const [tipodocFiador, setTipodocFiador] = useState("cedula");

  // Con el documento completo intenta precargar los datos de la última
  // solicitud del cliente (mismo endpoint de la venta financiada). Si el
  // endpoint falla se sigue digitando manualmente. Cédula y RNC buscan al
  // completarse; el pasaporte (largo variable) al salir del campo.
  const buscarCliente = async (ced, tipo) => {
    if (!documentoValido(ced, tipo) || cedulaBuscadaRef.current === ced) return;
    cedulaBuscadaRef.current = ced;
    try {
      const { ok, data } = await post("/api/ventaproducto_buscarcliente", { cedula: ced });
      if (!ok) return;
      setPendiente(toNum(data.pendiente));
      if (data.solicitud) {
        const s = data.solicitud;
        setForm((p) => ({
          ...p,
          nombres: s.nombres || p.nombres,
          apellidos: s.apellidos || p.apellidos,
          direccion: s.direccion || p.direccion,
          provincia: s.urb || p.provincia,
          telefono: s.telefono || p.telefono,
          celular: s.celular || p.celular,
          email: s.email || p.email,
          apodo: s.apodo || p.apodo,
          edad: s.edad != null ? String(s.edad) : p.edad,
        }));
      }
    } catch {
      // Precarga opcional: si falla, se sigue con el registro manual.
    }
  };

  // ---- Vista previa local de la cuota ----
  // Replica _calcular del backend: el interes se digita como % mensual y el
  // factor del periodo lo lleva a tasa por cuota. Sin la forma de pago no hay
  // previa: con Quincenas/Semanas/Dias el monto cambia por 2, 4 o 30.
  const previa = useMemo(() => {
    const r = calcularCuotas(
      toNum(form.monto),
      toNum(form.plazo),
      form.formadepago,
      toNum(form.interes),
      redondeo,
      { siempreTasa: true }
    );
    return r ? { cuota: r.valorcuota, deudatotal: r.deudatotal } : null;
  }, [form.monto, form.interes, form.plazo, form.formadepago, redondeo]);

  // ---- Autorización (mismo flujo/endpoints de la venta financiada) ----
  const [autOpen, setAutOpen] = useState(false);
  const [autMotivo, setAutMotivo] = useState("");
  const [autIdtext, setAutIdtext] = useState("");
  const [autStatus, setAutStatus] = useState(""); // "" | Progreso | Aprobado | Cancelado
  const [autComentario, setAutComentario] = useState("");
  const pollRef = useRef(null);

  const detenerPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };
  useEffect(() => detenerPolling, []);

  const cancelarAutorizacion = async () => {
    detenerPolling();
    if (autIdtext && autStatus === "Progreso") {
      await post("/api/ventaproducto_cancelarautorizacion", { idtext: autIdtext });
    }
    setAutOpen(false);
    setAutIdtext("");
    setAutStatus("");
    setAutComentario("");
  };

  // ---- Guardar ----
  const [guardando, setGuardando] = useState(false);

  const limpiarPantalla = () => {
    detenerPolling();
    setForm(FORM_INICIAL);
    setPendiente(0);
    cedulaBuscadaRef.current = "";
    setTipodoc("cedula");
    setTipodocFiador("cedula");
    setAutOpen(false);
    setAutIdtext("");
    setAutStatus("");
    setAutComentario("");
  };

  const guardar = async (idautorizacion = "") => {
    if (!documentoValido(form.cedula, tipodoc)) {
      showAlert({
        message: "El documento del cliente está incompleto (cédula, RNC o pasaporte)",
        color: "warning",
      });
      return;
    }
    if (toNum(form.monto) <= 0) {
      showAlert({ message: "El monto no puede estar en 0", color: "warning" });
      return;
    }
    if (!form.idcartera || !form.idoficial) {
      showAlert({
        message: "Debe seleccionar la cartera y el oficial de crédito",
        color: "warning",
      });
      return;
    }
    if (
      !form.fiador.trim() ||
      !form.fiadorced.trim() ||
      !form.fiadortel.trim() ||
      !form.fiadordir.trim()
    ) {
      showAlert({ message: "Los datos del fiador son obligatorios", color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const { ok, status, data } = await post("/api/prestamopersonal_guardar", {
        cedula: form.cedula.trim(),
        nombres: form.nombres.trim(),
        apellidos: form.apellidos.trim(),
        direccion: form.direccion.trim(),
        provincia: form.provincia,
        telefono: form.telefono.trim(),
        celular: form.celular.trim(),
        email: form.email.trim(),
        apodo: form.apodo.trim(),
        edad: form.edad,
        comentario: form.comentario.trim(),
        garantia: form.garantia.trim(),
        idcartera: form.idcartera,
        idoficial: form.idoficial,
        fiador: form.fiador.trim(),
        fiadorced: form.fiadorced.trim(),
        fiadortel: form.fiadortel.trim(),
        fiadordir: form.fiadordir.trim(),
        chasis: usaChasis ? form.chasis.trim() : "",
        monto: toNum(form.monto),
        formadepago: form.formadepago,
        fechaprimeracuota: elegirPrimeraCuota ? form.fechaprimeracuota : "",
        interes: toNum(form.interes),
        plazo: form.plazo,
        idautorizacion,
      });
      if (ok) {
        showAlert({
          message: `${data.ok || "Préstamo personal registrado"} — Préstamo #${
            data.noprest
          } · Cuota RD$ ${fmtMoney(data.valorcuota)} · Deuda total RD$ ${fmtMoney(
            data.deudatotal
          )}`,
          color: "success",
        });
        limpiarPantalla();
        return;
      }
      if (status === 409 && data.requiereAutorizacion) {
        setAutMotivo(data.Error || "El interés está fuera del rango permitido");
        setAutIdtext("");
        setAutStatus("");
        setAutComentario("");
        setAutOpen(true);
        return;
      }
      showAlert({ message: data.Error || "Error al guardar", color: "error" });
    } catch {
      showAlert({ message: "Error de red o servidor", color: "error" });
    } finally {
      setGuardando(false);
    }
  };

  const solicitarAutorizacion = async () => {
    const { ok, data } = await post("/api/ventaproducto_solicitarautorizacion", {
      descripcion: `Prestamo personal cliente ${form.nombres.trim()} ${form.apellidos.trim()} interes ${toNum(
        form.interes
      )}%`,
      datos: {
        cedula: form.cedula,
        nombre: form.nombres,
        apellido: form.apellidos,
        monto: form.monto,
        plazo: form.plazo,
        formadepago: form.formadepago,
        interes: form.interes,
      },
    });
    if (!ok) {
      showAlert({ message: data.Error || "No se pudo solicitar la autorización", color: "error" });
      return;
    }
    setAutIdtext(data.idtext);
    setAutStatus("Progreso");
    detenerPolling();
    pollRef.current = setInterval(async () => {
      const r = await post("/api/ventaproducto_estadoautorizacion", { idtext: data.idtext });
      if (!r.ok) return;
      const st = r.data.status;
      if (st === "Aprobado") {
        detenerPolling();
        setAutStatus("Aprobado");
        // Aprobada: se reintenta el guardado con la autorización.
        guardar(data.idtext);
      } else if (st === "Cancelado") {
        detenerPolling();
        setAutStatus("Cancelado");
        setAutComentario(r.data.comentario || "");
      }
    }, 5000);
  };

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Préstamos Personales"
        subtitle="Registra un préstamo de dinero sin vehículo: cliente, condiciones, cartera, oficial y fiador"
        cta={
          <Stack direction="row" spacing={1.2}>
            <Button
              startIcon={<RestartAltIcon />}
              onClick={limpiarPantalla}
              sx={btnOutline(FP.color.navy)}
            >
              Nuevo
            </Button>
            <Button
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              onClick={() => guardar()}
              disabled={guardando || !config}
              sx={btnSolid(FP.color.navy, FP.color.navySoft)}
            >
              Guardar
            </Button>
          </Stack>
        }
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Cliente */}
        <Box sx={sectionBox}>
          <SectionTitle
            icon={PersonOutlineIcon}
            color={FP.color.accentBlue}
            extra={
              pendiente > 0 && (
                <Chip
                  label={`Balance pendiente RD$ ${fmtMoney(pendiente)}`}
                  sx={{ ml: "auto", bgcolor: "#FDECEA", color: FP.color.danger, fontWeight: 700 }}
                />
              )
            }
          >
            Cliente
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={3}>
              <DocumentoField
                value={form.cedula}
                tipo={tipodoc}
                onChangeValor={(v) => {
                  setForm((p) => ({ ...p, cedula: v }));
                  if (tipodoc !== "pasaporte") buscarCliente(v, tipodoc);
                }}
                onChangeTipo={setTipodoc}
                onBlur={() => buscarCliente(form.cedula, tipodoc)}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Nombres"
                value={form.nombres}
                onChange={setF("nombres")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={5}>
              <TextField
                fullWidth
                label="Apellidos"
                value={form.apellidos}
                onChange={setF("apellidos")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Dirección"
                value={form.direccion}
                onChange={setF("direccion")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Autocomplete
                options={provincias}
                value={form.provincia || null}
                onChange={(_, v) => setForm((p) => ({ ...p, provincia: v || "" }))}
                renderInput={(params) => <TextField {...params} label="Provincia" sx={fpInputSx} />}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Teléfono"
                value={form.telefono}
                onChange={setF("telefono")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <TextField
                fullWidth
                label="Celular"
                value={form.celular}
                onChange={setF("celular")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Email"
                value={form.email}
                onChange={setF("email")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={2}>
              <TextField
                fullWidth
                type="number"
                label="Edad"
                value={form.edad}
                onChange={setF("edad")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={6} md={4}>
              <TextField
                fullWidth
                label="Apodo"
                value={form.apodo}
                onChange={setF("apodo")}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Préstamo */}
        <Box sx={sectionBox}>
          <SectionTitle
            icon={PaymentsOutlinedIcon}
            color={FP.color.success}
            extra={
              sinCatalogos && (
                <Chip
                  label="No hay carteras/oficiales activos — pida al administrador crearlos"
                  sx={{ ml: "auto", bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
                />
              )
            }
          >
            Préstamo
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="number"
                label="Monto"
                value={form.monto}
                onChange={setF("monto")}
                sx={fpInputSx}
                InputProps={{
                  startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                select
                fullWidth
                label="Forma de pago"
                value={form.formadepago}
                onChange={setF("formadepago")}
                sx={fpInputSx}
              >
                {formasPago.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {elegirPrimeraCuota && (
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  type="date"
                  label="Fecha primera cuota"
                  value={form.fechaprimeracuota}
                  onChange={setF("fechaprimeracuota")}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: mananaISO(), max: unMesISO() }}
                  sx={fpInputSx}
                  helperText="Vacía = automática; hasta un mes"
                />
              </Grid>
            )}
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="number"
                label="Interés (mensual)"
                value={form.interes}
                onChange={setF("interes")}
                sx={fpInputSx}
                InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                helperText={`Rango sin autorización: ${interesminimo}% – 20%; más de 20% pide autorización`}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField
                fullWidth
                type="number"
                label="Plazo (cuotas)"
                value={form.plazo}
                onChange={setF("plazo")}
                sx={fpInputSx}
              />
            </Grid>
            {redondeo && (
              <Grid item xs={6} sm={3}>
                <CampoCuota
                  editable
                  label="Valor cuota"
                  cuota={previa ? previa.cuota : null}
                  financia={toNum(form.monto)}
                  plazo={toNum(form.plazo)}
                  formadepago={form.formadepago}
                  siempreTasa
                  onInteres={(interes) => setForm((p) => ({ ...p, interes }))}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            {previa && (
              <Grid item xs={12}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip
                    label={`Cuota RD$ ${fmtMoney(previa.cuota)}`}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={`Deuda total RD$ ${fmtMoney(previa.deudatotal)}`}
                    sx={{
                      bgcolor: FP.color.surfaceAlt,
                      color: FP.color.textSecondary,
                      fontWeight: 700,
                    }}
                  />
                </Stack>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Cartera"
                value={form.idcartera}
                onChange={setF("idcartera")}
                sx={fpInputSx}
              >
                {carteras.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.descripcion}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Oficial de crédito"
                value={form.idoficial}
                onChange={setF("idoficial")}
                sx={fpInputSx}
              >
                {oficiales.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Garantía"
                value={form.garantia}
                onChange={setF("garantia")}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Comentario"
                value={form.comentario}
                onChange={setF("comentario")}
                inputProps={{ maxLength: 1000 }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Fiador */}
        <Box sx={{ ...sectionBox, mb: usaChasis ? 2.4 : 0 }}>
          <SectionTitle icon={AssignmentIndOutlinedIcon} color={FP.color.danger}>
            Fiador
          </SectionTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Nombre fiador"
                value={form.fiador}
                onChange={setF("fiador")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DocumentoField
                label="Documento fiador"
                value={form.fiadorced}
                tipo={tipodocFiador}
                onChangeValor={(v) => setForm((p) => ({ ...p, fiadorced: v }))}
                onChangeTipo={setTipodocFiador}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Teléfono fiador"
                value={form.fiadortel}
                onChange={setF("fiadortel")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dirección fiador"
                value={form.fiadordir}
                onChange={setF("fiadordir")}
                sx={fpInputSx}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Garantía con chasis (solo tenants con usachasisenpersonal='S') */}
        {usaChasis && (
          <Box sx={{ ...sectionBox, mb: 0 }}>
            <SectionTitle icon={DirectionsCarFilledOutlinedIcon} color={FP.color.navy}>
              Garantía con Chasis
            </SectionTitle>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Chasis (garantía)"
                  value={form.chasis}
                  onChange={setF("chasis")}
                  helperText="El vehículo quedará marcado como vendido"
                  sx={fpInputSx}
                />
              </Grid>
            </Grid>
          </Box>
        )}
      </Box>

      {/* Dialog de autorización */}
      <Dialog
        open={autOpen}
        onClose={() => {}}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, display: "flex", alignItems: "center", gap: 1.2 }}>
          <GppMaybeOutlinedIcon sx={{ color: FP.color.warning }} />
          Se requiere autorización
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "0.9rem", color: FP.color.textSecondary, mb: 2 }}>
            {autMotivo}
          </Typography>

          {autStatus === "" && (
            <Typography sx={{ fontSize: "0.84rem", color: FP.color.textMuted }}>
              Haz clic en &quot;Solicitar autorización&quot; para enviar la petición al
              administrador.
            </Typography>
          )}
          {autStatus === "Progreso" && (
            <Stack direction="row" spacing={1.4} alignItems="center">
              <CircularProgress size={18} />
              <Chip
                label="Esperando autorización..."
                sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
              />
            </Stack>
          )}
          {autStatus === "Aprobado" && (
            <Stack direction="row" spacing={1.4} alignItems="center">
              <CheckCircleOutlineIcon sx={{ color: FP.color.success }} />
              <Typography sx={{ fontSize: "0.9rem", color: FP.color.success, fontWeight: 700 }}>
                Autorización aprobada. Registrando el préstamo…
              </Typography>
            </Stack>
          )}
          {autStatus === "Cancelado" && (
            <Typography sx={{ fontSize: "0.9rem", color: FP.color.danger, fontWeight: 700 }}>
              Autorización declinada{autComentario ? ` — mensaje: ${autComentario}` : ""}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={cancelarAutorizacion} sx={btnGhost}>
            Cerrar
          </Button>
          {autStatus === "" && (
            <Button
              disableElevation
              onClick={solicitarAutorizacion}
              sx={btnSolid(FP.color.warning, "#B45309")}
            >
              Solicitar autorización
            </Button>
          )}
          {autStatus === "Aprobado" && (
            <Button
              disableElevation
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
              disabled={guardando}
              onClick={() => guardar(autIdtext)}
              sx={btnSolid(FP.color.success, FP.color.successDark)}
            >
              Guardar con autorización
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
